import { differenceInDays, differenceInHours } from 'date-fns';

import type APIAddress from '~/types/APIAddress';
import type Address from '~/types/Address';
import type Order from '~/types/Order';

import { type OrderV2ShipmentAddress } from '~/modules/orders/types/ordersV2.type';
import { type APIError } from '~/types/APIError';
import { isAddressIncomplete } from '~/types/Address';
import { getCustomsPhoneErrors } from '~/types/CustomsDeclaration';
import { type OrderSelection, type SelectedOrdersAddresses } from '~/types/Order';
import { isFulfilled, requiresCustomsDeclaration as isOrderRequiresCustoms } from '~/types/Order';
import { requiresCustomsDeclaration, type BatchEntryExtended } from '~/types/OrderBatchEntry';

export type SelectedOrderErrorKeys =
  | 'addressesErrors'
  | 'carrierParcelNoRatesErrors'
  | 'customsErrors'
  | 'dateErrors'
  | 'packageErrors'
  | 'recipientErrors'
  | 'returnErrors'
  | 'senderErrors';

type SelectedOrdersErrorsCountKey = Exclude<SelectedOrderErrorKeys, 'addressesErrors'>;

export type SelectedOrdersErrorsCount = { [k in SelectedOrdersErrorsCountKey]: number };

export const hasSelectedOrdersErrors = (selectedOrdersErrors: SelectedOrdersErrorsCount) =>
  Object.keys(selectedOrdersErrors).some((errorKey) => selectedOrdersErrors[errorKey] > 0);

export const getOrderErrors = (
  selectedOrders: OrderSelection,
  batchEntries: BatchEntryExtended[],
  ordersData: {
    [orderId: string]: Order;
  },
  selectedOrdersAddresses: SelectedOrdersAddresses,
  defaultUserAddress?: APIAddress,
  ratesByShipmentObjectID?: HashMap<string, readonly string[]>,
) => {
  let carrierParcelNoRatesErrors = 0;
  let customsErrors = 0;
  let dateErrors = 0;
  let senderErrors = 0;
  let returnErrors = 0;
  let recipientErrors = 0;
  let packageErrors = 0;

  const { returnAddresses, senderAddresses } = selectedOrdersAddresses;

  const hasNoSenderAddresses = senderAddresses.length === 0;
  const hasIncompleteSenderAddress = senderAddresses.some((address) =>
    isAddressIncomplete(address),
  );

  if (hasNoSenderAddresses || hasIncompleteSenderAddress) senderErrors++;

  const hasNoReturnAddresses = returnAddresses.length === 0;
  const hasIncompleteReturnAddress = returnAddresses.some((address) =>
    isAddressIncomplete(address),
  );

  if (hasNoReturnAddresses || hasIncompleteReturnAddress) returnErrors++;

  Object.entries(selectedOrders).forEach(([orderId, orderBatchEntryIds]) => {
    if (orderBatchEntryIds.length) return;

    const order = ordersData[orderId];
    if (!order) return;

    if (orderHasCustomsError(order, undefined, defaultUserAddress)) customsErrors++;

    if (orderHasShipmentDateError()) dateErrors++;

    if (
      orderHasRecipientError({
        addressFrom: order.addressFrom,
        addressTo: order.toAddress,
      })
    )
      recipientErrors++;

    if (orderHasPackageError(order)) packageErrors++;
  });

  batchEntries.forEach((batchEntry) => {
    const order = ordersData[batchEntry.orderObjectId];
    if (!order) return;
    if (orderHasCarrierParcelNoRatesError(batchEntry, ratesByShipmentObjectID))
      carrierParcelNoRatesErrors++;

    if (orderHasCustomsError(order, batchEntry, defaultUserAddress)) customsErrors++;

    if (orderHasShipmentDateError(batchEntry)) dateErrors++;

    if (
      orderHasRecipientError({
        addressFrom: batchEntry ? batchEntry.addressFrom : order.addressFrom,
        addressTo: batchEntry ? batchEntry.addressTo : order.toAddress,
      })
    )
      recipientErrors++;

    if (orderHasPackageError(order, batchEntry)) packageErrors++;
  });

  return {
    carrierParcelNoRatesErrors,
    customsErrors,
    dateErrors,
    packageErrors,
    recipientErrors,
    returnErrors,
    senderErrors,
  };
};

export type SelectedOrdersErrors = ReturnType<typeof getOrderErrors>;

export const orderHasCarrierParcelNoRatesError = (
  batchEntry: BatchEntryExtended,
  ratesByShipmentObjectID?: HashMap<string, readonly string[]>,
) =>
  batchEntry.parcel?.carrierParcelTemplate &&
  batchEntry.shipmentObjectId &&
  ratesByShipmentObjectID &&
  batchEntry.shipmentObjectId in ratesByShipmentObjectID &&
  ratesByShipmentObjectID[batchEntry.shipmentObjectId].length === 0;

export const orderHasCustomsError = (
  order: Order,
  batchEntry?: BatchEntryExtended,
  defaultUserAddress?: APIAddress,
) => {
  const isBypassCustoms = !!batchEntry?.extras?.isBypassCustoms;
  const requiresCustoms = batchEntry
    ? requiresCustomsDeclaration(batchEntry) && !isBypassCustoms
    : isOrderRequiresCustoms(order, defaultUserAddress);
  const customsItems = batchEntry?.customsItems?.length ? batchEntry.customsItems : [];
  return requiresCustoms && !isFulfilled(order) && !customsItems.length;
};

export const orderHasShipmentDateError = (batchEntry?: BatchEntryExtended) => {
  const now = new Date();
  const shipmentDate = batchEntry?.shipmentDate ? batchEntry.shipmentDate.toDate() : now;
  const isWithinPast24Hours = differenceInHours(now, shipmentDate) <= 24;
  const isWithinNext5Days = differenceInDays(shipmentDate, now) <= 5;
  return !isWithinPast24Hours || !isWithinNext5Days;
};

export const orderHasPackageError = (order: Order | undefined, batchEntry?: BatchEntryExtended) => {
  const hasParcelError = !batchEntry?.parcel;
  const hasParcelWeightErrors = batchEntry
    ? !(Number(batchEntry.parcel?.weight) > 0 && batchEntry.parcel?.weightUnit)
    : !(Number(order?.parcel?.weight) > 0 && order?.parcel?.weightUnit);
  const batchEntryErrors = batchEntry?.errors ? [...batchEntry.errors] : [];
  const orderErrors =
    order &&
    (batchEntry?.partialFulfillmentRank === 0 || order.partialFulfillmentRequests?.length === 0)
      ? [...(order.errors || [])]
      : [];
  const errors: APIError[] = batchEntryErrors.length ? batchEntryErrors : orderErrors;
  const hasExceededPackageWeightError = !!errors.find(
    (error) => error.code === 'order_weight_exceeded',
  );

  return hasParcelError || hasParcelWeightErrors || hasExceededPackageWeightError;
};

export const orderHasRecipientError = ({
  addressFrom,
  addressTo,
}: {
  addressFrom?: Address | OrderV2ShipmentAddress | null;
  addressTo?: Address | OrderV2ShipmentAddress | null;
}) => {
  const { hasPhoneNumberRequiredError } = getCustomsPhoneErrors({ addressFrom, addressTo });
  if (hasPhoneNumberRequiredError) return true;

  if (isAddressIncomplete(addressTo)) return true;
  return false;
};

export const getIncompleteOrderErrorKeys = (
  errors: SelectedOrdersErrorsCount,
): SelectedOrderErrorKeys[] => {
  if (!errors) return [];

  const errorList: SelectedOrderErrorKeys[] = [];
  for (const [key, value] of Object.entries(errors))
    if (value >= 1) errorList.push(key as keyof SelectedOrdersErrorsCount);

  return errorList;
};
